import React, {useEffect, useState} from "react";
import {db, functions} from '../../firebase/FirebaseProvider'
import {doc, collection, getDoc, getDocs, query, where, updateDoc} from 'firebase/firestore'
import {Location, User} from "../types/Types";
import {Button, Card, Col, Form, Row, Dropdown, InputGroup} from "react-bootstrap";
import {useNavigate, useParams} from "react-router-dom";
import {httpsCallable} from "@firebase/functions";
import {getAuth, onAuthStateChanged} from "firebase/auth";
import PageTracking from "../auth/PageTracking";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faTimes} from "@fortawesome/free-solid-svg-icons";

function Admin() {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();

    const [currentUser, setCurrentUser] = useState<User | undefined>(undefined);
    const [location, setLocation] = useState<Location | undefined>(undefined);

    const [name, setName] = useState('');
    const [addressLine1, setAddressLine1] = useState('');
    const [city, setCity] = useState('');
    const [county, setCounty] = useState('');
    const [postcode, setPostcode] = useState('');
    const [country, setCountry] = useState('');
    const [lat, setLat] = useState('');
    const [lng, setLng] = useState('');
    const [hotel, setHotel] = useState<boolean | undefined>(false);
    const [airport, setAirport] = useState<boolean | undefined>(false);
    const [holidayPark, setHolidayPark] = useState<boolean | undefined>(false);
    const [isUniversity, setIsUniversity] = useState<boolean | undefined>(false);
    const [trainStation, setTrainStation] = useState<boolean | undefined>(false);
    const [pubIsTemporaryClosed, setPubIsTemporaryClosed] = useState<boolean  | undefined>(false);
    const [pubIsClosed, setPubIsClosed] = useState<boolean | undefined>(false);
    const [openingDate, setOpeningDate] = useState<string>('');
    const [closingDate, setClosingDate] = useState<string>('');
    const [editMode, setEditMode] = useState<boolean | undefined>(true);
    const [saving, setSaving] = useState<boolean | undefined>(false);

    useEffect(() => {
        (async () => {
            const auth = getAuth();
            onAuthStateChanged(auth, async (currentUser) => {
                if (!currentUser) {
                    setCurrentUser(undefined);
                    return;
                }
                const userCollection = query(collection(db, 'users'), where('authId', '==', currentUser.uid));
                const userCollectionSnap = await getDocs(userCollection);
                userCollectionSnap.forEach(async (userDoc) => {
                    setCurrentUser(userDoc.data() as User);
                    if(!userDoc.data().admin) {
                        navigate('/');
                    }
                    if(id) {
                        await loadLocation(id);
                    } else {
                        setEditMode(false);
                    }
                    return;
                });
            });
        })();
    }, [id, navigate]);

    const loadLocation = async (locationId: string) => {
        const locationRef = doc(db, 'locations', locationId);
        const docSnap = await getDoc(locationRef);
        if (docSnap.exists()) {
            const location = docSnap.data() as Location;
            if(!location) {
                return;
            }
            setLocation(location);
            setName(location.name);
            setAddressLine1(location.addressLine1);
            setCity(location.city);
            setCounty(location.county);
            setPostcode(location.postcode);
            setCountry(location.country || '');
            setLng(location.lng);
            setLat(location.lat);
            setAirport(location.airport);
            setHotel(location.hotel);
            setHolidayPark(location.holidayPark);
            setIsUniversity(location.isUniversity || false);
            setTrainStation(location.trainStation || false);
            setPubIsTemporaryClosed(location.pubIsTemporaryClosed);
            setPubIsClosed(location.pubIsClosed);
            setOpeningDate(location.openingDate || '');
            setClosingDate(location.closingDate || '');
         }
    }

    const generateAddress = () => {
        var address = '';
        if(addressLine1) {
            address = addressLine1;
        }
        if(addressLine1 && city) {
            address = address + ", ";
        }
        if(city) {
            address = address + city;
        }
        if(county) {
            address = address + ", " + county;
        }
        if(postcode) {
            address = address + ", " + postcode;
        }
        return address;
    }

    const saveLocation = () => {
        setSaving(true);
        if(!name || !addressLine1 || !city || !postcode || !country || !lat || !lng || !openingDate) {
            console.log("something required was null");
            setSaving(false);
            return;
        }
        if(pubIsClosed && !closingDate) {
            alert("Please enter a closing date when marking a pub as closed.");
            setSaving(false);
            return;
        }
        let locationId = '';
        if(location) {
            locationId = location.id;
        }
        var editLocation = httpsCallable(functions, 'editLocation');
        editLocation({location: {
                id: locationId,
                name,
                addressLine1,
                city,
                county,
                postcode,
                country,
                lat,
                lng,
                hotel,
                airport,
                holidayPark,
                isUniversity: isUniversity || false,
                trainStation: trainStation || false,
                pubIsTemporaryClosed,
                pubIsClosed,
                openingDate,
                closingDate,
                address: generateAddress()
            }}).then(async r => {
            const result = r.data as { locationId?: string } | undefined;
            const savedLocationId = result?.locationId || locationId;
            if (savedLocationId) {
                const locationRef = doc(db, 'locations', savedLocationId);
                await updateDoc(locationRef, {
                    trainStation: trainStation || false,
                });
            }
            navigate('/pubs');
        }).catch(error => {
            console.error("Error saving location:", error);
            setSaving(false);
        });
    }

    return (
        <>
            <PageTracking />
            <Card className="leaderboard my-3">
                <Card.Header>
                    {location && location.id && <div>Edit Location</div>}
                    {!location && <div>Create Location</div>}
                </Card.Header>
                <Card.Body>
                    <Row className={""}>
                        <Col className="">
                            <Form>

                                {location && location.id && <Form.Group className="mb-3" controlId="formBasicText">
                                    <Form.Label>ID</Form.Label>
                                    <Form.Control type="text" placeholder="ID" value={location.id} disabled/>
                                </Form.Group>}

                                {(location || !editMode) && <>

                                    <Form.Group className="mb-3" controlId="formBasicText">
                                        <Form.Label>Name</Form.Label>
                                        <Form.Control type="text" placeholder="Location name" value={name} onChange={e => setName(e.target.value)}/>
                                    </Form.Group>

                                    <Form.Group className="mb-3" controlId="formBasicText">
                                        <Form.Label>Address</Form.Label>
                                        <Form.Control type="text" placeholder="Address line 1" value={addressLine1} onChange={e => setAddressLine1(e.target.value)}/>
                                    </Form.Group>

                                    <Form.Group className="mb-3" controlId="formBasicText">
                                        <Form.Label>City</Form.Label>
                                        <Form.Control type="text" placeholder="City" value={city}  onChange={e => setCity(e.target.value)}/>
                                    </Form.Group>

                                    <Form.Group className="mb-3" controlId="formBasicText">
                                        <Form.Label>County</Form.Label>
                                        <Form.Control type="text" placeholder="County" value={county}  onChange={e => setCounty(e.target.value)}/>
                                    </Form.Group>

                                    <Form.Group className="mb-3" controlId="formBasicText">
                                        <Form.Label>Postcode</Form.Label>
                                        <Form.Control type="text" placeholder="Postcode" value={postcode}  onChange={e => setPostcode(e.target.value)}/>
                                    </Form.Group>

                                    <Form.Group className="mb-3" controlId="formBasicSelect">
                                        <Form.Label>Country <span style={{ color: 'red' }}>*</span></Form.Label>
                                        <Dropdown className="w-100">
                                            <Dropdown.Toggle 
                                                variant="light" 
                                                className="w-100 text-start form-control"
                                                style={{ 
                                                    backgroundColor: 'white',
                                                    color: country ? 'black' : '#6c757d',
                                                    borderColor: '#ced4da',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center'
                                                }}
                                            >
                                                <span>{country || 'Please select...'}</span>
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu className="w-100">
                                                <Dropdown.Item onClick={() => setCountry('England')}>
                                                    England
                                                </Dropdown.Item>
                                                <Dropdown.Item onClick={() => setCountry('Scotland')}>
                                                    Scotland
                                                </Dropdown.Item>
                                                <Dropdown.Item onClick={() => setCountry('Wales')}>
                                                    Wales
                                                </Dropdown.Item>
                                                <Dropdown.Item onClick={() => setCountry('Northern Ireland')}>
                                                    Northern Ireland
                                                </Dropdown.Item>
                                                <Dropdown.Item onClick={() => setCountry('Ireland')}>
                                                    Ireland
                                                </Dropdown.Item>
                                                <Dropdown.Item onClick={() => setCountry('Isle of Man')}>
                                                    Isle of Man
                                                </Dropdown.Item>
                                                <Dropdown.Item onClick={() => setCountry('Spain')}>
                                                    Spain
                                                </Dropdown.Item>
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </Form.Group>

                                    <Form.Group className="mb-3" controlId="formBasicText">
                                        <Form.Label>Latitude</Form.Label>
                                        <Form.Control type="text" placeholder="Latitude" value={lat}  onChange={e => setLat(e.target.value)}/>
                                    </Form.Group>

                                    <Form.Group className="mb-3" controlId="formBasicText">
                                        <Form.Label>Longitude</Form.Label>
                                        <Form.Control type="text" placeholder="Longitude" value={lng} onChange={e => setLng(e.target.value)}/>
                                    </Form.Group>

                                    <Form.Group className="mb-3" controlId="formBasicDate">
                                        <Form.Label>Opening Date <span style={{ color: 'red' }}>*</span></Form.Label>
                                        <Form.Control type="date" value={openingDate} onChange={e => setOpeningDate(e.target.value)}/>
                                        <Form.Text className="text-muted">
                                            The date this pub opened. If unknown try searching online or just give a best guess.
                                        </Form.Text>
                                    </Form.Group>

                                    <Form.Group className="mb-3" controlId="formBasicClosingDate">
                                        <Form.Label>Closing Date</Form.Label>
                                        <InputGroup>
                                            <Form.Control type="date" value={closingDate} onChange={e => setClosingDate(e.target.value)}/>
                                            <Button
                                                type="button"
                                                variant="primary"
                                                onClick={() => setClosingDate('')}
                                                disabled={!closingDate}
                                                aria-label="Clear closing date"
                                                title="Clear closing date"
                                            >
                                                <FontAwesomeIcon icon={faTimes} />
                                            </Button>
                                        </InputGroup>
                                        <Form.Text className="text-muted">
                                            Optional: The date this pub closed permanently. Leave blank if still open.
                                        </Form.Text>
                                    </Form.Group>

                                    <Form.Group className="mb-3" controlId="formBasicCheckbox0">
                                        <Form.Check type="checkbox" label="Is a hotel?" checked={hotel} onChange={e => setHotel(e.target.checked)}/>
                                    </Form.Group>

                                    <Form.Group className="mb-3" controlId="formBasicCheckbox1">
                                        <Form.Check type="checkbox" label="Is in an airport?" checked={airport} onChange={e => setAirport(e.target.checked)}/>
                                    </Form.Group>

                                    <Form.Group className="mb-3" controlId="formBasicCheckbox1a">
                                        <Form.Check type="checkbox" label="In a holiday park?" checked={holidayPark} onChange={e => setHolidayPark(e.target.checked)}/>
                                    </Form.Group>

                                    <Form.Group className="mb-3" controlId="formBasicCheckbox1b">
                                        <Form.Check type="checkbox" label="Is a university pub?" checked={isUniversity} onChange={e => setIsUniversity(e.target.checked)}/>
                                    </Form.Group>

                                    <Form.Group className="mb-3" controlId="formBasicCheckbox1c">
                                        <Form.Check type="checkbox" label="Is at a train station?" checked={trainStation} onChange={e => setTrainStation(e.target.checked)}/>
                                    </Form.Group>

                                    <Form.Group className="mb-3" controlId="formBasicCheckbox2">
                                        <Form.Check type="checkbox" label="Renovating?" checked={pubIsTemporaryClosed} onChange={e => setPubIsTemporaryClosed(e.target.checked)}/>
                                    </Form.Group>

                                    <Form.Group className="mb-3" controlId="formBasicCheckbox3">
                                        <Form.Check type="checkbox" label="Closed?" checked={pubIsClosed} onChange={e => setPubIsClosed(e.target.checked)}/>
                                    </Form.Group>

                                </>}

                                <div style={{textAlign: "right"}}>
                                    <Button disabled={saving} variant="primary btn cursor-pointer" onClick={() => saveLocation()}>
                                        Save
                                    </Button>
                                </div>
                            </Form>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        </>
    );
}

export default Admin;
